import { UploadType } from 'further-types/files';
import FileDrop from 'further-ui/components/FileDrop';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { compact } from 'lodash';
import useFileUpload from 'hooks/ui/useFileUpload';

export type FileUploadProps = {
  uploadType: UploadType;
  files: Array<{
    fileName: string;
    filePath: string;
    previewUri?: string;
  }>;
  onChange: (
    files: Array<{
      fileName: string;
      filePath: string;
      previewUri?: string;
    }>,
  ) => void;
  onDeleteFile: (index: number) => void;
  onUploadError?: (message: string) => void;
  hideInputTitle?: boolean;
  inputTitle?: string;
  inputTitleTooltip?: string;
  inputTitleDotColor?: string;
  error?: boolean;
  helperText?: string;
  maxFiles?: number;
  height?: number;
};

const FileUpload: React.FC<FileUploadProps> = ({
  uploadType,
  files,
  onChange,
  onDeleteFile,
  onUploadError,
  hideInputTitle,
  inputTitle = 'Upload',
  inputTitleTooltip,
  inputTitleDotColor,
  error,
  helperText,
  maxFiles,
  height,
}) => {
  const { uploadFile } = useFileUpload();

  return (
    <FileDrop
      inputTitle={hideInputTitle ? undefined : inputTitle}
      inputTitleTooltip={inputTitleTooltip}
      inputTitleDotColor={inputTitleDotColor}
      error={error}
      helperText={helperText}
      files={files.map((file) => ({
        ...file,
        isUploading: file.filePath === '',
      }))}
      onDeleteFile={onDeleteFile}
      onDropFile={async (droppedFiles) => {
        onChange([
          ...files,
          ...droppedFiles.map((file) => ({
            fileName: file.fileName,
            filePath: '',
            previewUri: file.previewUri,
          })),
        ]);

        const uploadedFiles = compact(
          await Promise.all(
            droppedFiles.map(async (droppedFile) => {
              const { data, error } = await uploadFile(
                droppedFile.fileData,
                uploadType,
              );
              if (error && onUploadError) {
                onUploadError(error.message);
              }
              if (!data) return undefined;
              return { name: data.name, path: data.filePath };
            }),
          ),
        );

        onChange([
          ...files,
          ...uploadedFiles.map((file) => ({
            fileName: file.name ?? '',
            filePath: file.path,
            previewUri: '',
          })),
        ]);
      }}
      supportedFormats={getValidFileTypesForUploadType(uploadType)}
      sizeRestriction={{
        unit: 'mb',
        value: getMaxSizeForUploadType(uploadType, 'mb'),
      }}
      maxFiles={maxFiles}
      height={height}
    />
  );
};

export default FileUpload;
